const E164_MAX_LENGTH = 16;

const PHONE_COUNTRIES = [
  { code: 'GB', dial: '44', name: 'UK', trunk: true, min: 9, max: 10 },
  { code: 'IE', dial: '353', name: 'Ireland', trunk: true, min: 7, max: 9 },
  { code: 'AT', dial: '43', name: 'Austria', trunk: true, min: 4, max: 13 },
  { code: 'BE', dial: '32', name: 'Belgium', trunk: true, min: 8, max: 9 },
  { code: 'DK', dial: '45', name: 'Denmark', trunk: false, min: 8, max: 8 },
  { code: 'FR', dial: '33', name: 'France', trunk: true, min: 9, max: 9 },
  { code: 'DE', dial: '49', name: 'Germany', trunk: true, min: 6, max: 11 },
  { code: 'IT', dial: '39', name: 'Italy', trunk: false, min: 9, max: 11 },
  { code: 'LU', dial: '352', name: 'Luxembourg', trunk: false, min: 6, max: 9 },
  { code: 'NL', dial: '31', name: 'Netherlands', trunk: true, min: 9, max: 9 },
  { code: 'NO', dial: '47', name: 'Norway', trunk: false, min: 8, max: 8 },
  { code: 'PL', dial: '48', name: 'Poland', trunk: false, min: 9, max: 9 },
  { code: 'PT', dial: '351', name: 'Portugal', trunk: false, min: 9, max: 9 },
  { code: 'ES', dial: '34', name: 'Spain', trunk: false, min: 9, max: 9 },
  { code: 'SE', dial: '46', name: 'Sweden', trunk: true, min: 7, max: 9 },
  { code: 'CH', dial: '41', name: 'Switzerland', trunk: true, min: 9, max: 9 },
  { code: 'US', dial: '1', name: 'USA / Canada', trunk: false, min: 10, max: 10 },
];

/** Default selection. */
const DEFAULT_COUNTRY = 'GB';

// Longest dial code first, so "+353" is never read as "+35" or "+3".
const BY_DIAL_LENGTH = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

const UK_MOBILE = /^07(?:[1-5]\d{8}|[789]\d{8}|624\d{6})$/;
const UK_LANDLINE_01 = /^01\d{8,9}$/;
const UK_LANDLINE_02 = /^02\d{9}$/;
const UK_NON_GEOGRAPHIC = /^03\d{9}$/;

/** Country entry for a selector value, or null when the code is not listed. */
function findCountry(code) {
  return PHONE_COUNTRIES.find((c) => c.code === code) || null;
}

function sanitisePhone(raw) {
  const value = String(raw === null || raw === undefined ? '' : raw).trim();
  if (!value) return '';
  const hasPlus = value.startsWith('+');
  const digits = value.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
}

function inRange(nsn, entry) {
  return nsn.length >= entry.min && nsn.length <= entry.max;
}

/**
 * Reduce any spelling of a number to its national significant digits:
 * "+44...", "0044...", "44..." and the trunk "0..." all collapse to the same
 * value. A bare dial code is only stripped when doing so yields a plausible
 * number and keeping it does not, so a national number that happens to start
 * with its own dial code survives.
 */
function toNationalSignificant(sanitised, entry) {
  let digits = sanitised.startsWith('+') ? sanitised.slice(1) : sanitised;
  let hadCountryCode = false;

  // An explicit prefix names a country. When it is not the selected one the
  // value is not this country's number, and must never be read as if it were.
  if (sanitised.startsWith('+') && !digits.startsWith(entry.dial)) return null;
  if (digits.startsWith('00') && !digits.startsWith(`00${entry.dial}`)) return null;

  if (digits.startsWith(`00${entry.dial}`)) {
    digits = digits.slice(2 + entry.dial.length);
    hadCountryCode = true;
  } else if (sanitised.startsWith('+') && digits.startsWith(entry.dial)) {
    digits = digits.slice(entry.dial.length);
    hadCountryCode = true;
  } else if (entry.trunk && digits.startsWith('0')) {
    return digits.slice(1);
  } else if (digits.startsWith(entry.dial)) {
    const rest = digits.slice(entry.dial.length);
    if (inRange(rest, entry) && !inRange(digits, entry)) {
      digits = rest;
      hadCountryCode = true;
    }
  }

  // A trunk zero left in after the country code is redundant.
  if (hadCountryCode && entry.trunk && digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

/** True when the 0-prefixed national value is a UK number we accept. */
function isValidUkNational(national) {
  return (
    UK_MOBILE.test(national) ||
    UK_LANDLINE_01.test(national) ||
    UK_LANDLINE_02.test(national) ||
    UK_NON_GEOGRAPHIC.test(national)
  );
}

/**
 * Convert a typed value to canonical E.164 for the selected country.
 * Returns { e164, reason }; e164 is null when the value is not usable.
 * Already canonical values pass through unchanged, which the profile save
 * depends on because it posts every field back on each save.
 */
function toE164(raw, country = DEFAULT_COUNTRY) {
  const sanitised = sanitisePhone(raw);
  if (!sanitised || sanitised === '+') return { e164: null, reason: 'empty' };

  const entry = findCountry(country);
  if (!entry) return { e164: null, reason: 'wrong-country' };

  const nsn = toNationalSignificant(sanitised, entry);
  if (nsn === null) return { e164: null, reason: 'wrong-country' };
  if (!nsn) return { e164: null, reason: 'empty' };

  if (entry.code === 'GB') {
    if (!isValidUkNational(`0${nsn}`)) return { e164: null, reason: 'not-uk-number' };
  } else if (nsn.length < entry.min) {
    return { e164: null, reason: 'too-short' };
  } else if (nsn.length > entry.max) {
    return { e164: null, reason: 'too-long' };
  }

  const e164 = `+${entry.dial}${nsn}`;
  if (e164.length > E164_MAX_LENGTH) return { e164: null, reason: 'too-long' };
  return { e164, reason: null };
}

/** True when the value converts to a valid canonical number. */
function isValidPhone(raw, country = DEFAULT_COUNTRY) {
  return toE164(raw, country).e164 !== null;
}

/** Human readable message for a failure reason. */
function phoneErrorMessage(reason, country = DEFAULT_COUNTRY) {
  const entry = findCountry(country);
  const where = entry ? ` for ${entry.name}` : '';
  switch (reason) {
    case 'empty':
      return 'Phone number is required';
    case 'not-uk-number':
      return 'Enter a UK mobile or landline';
    case 'wrong-country':
      return 'Number does not match the selected country';
    case 'too-short':
      return `Phone number is too short${where}`;
    case 'too-long':
      return `Phone number is too long${where}`;
    default:
      return 'Valid phone number required';
  }
}

/**
 * Split a stored canonical value back into the country selection and the text
 * to show in the input, so an edit form restores the selector position.
 */
function splitE164(value) {
  const sanitised = sanitisePhone(value);
  if (!sanitised) return { country: DEFAULT_COUNTRY, national: '' };

  if (sanitised.startsWith('+')) {
    const digits = sanitised.slice(1);
    const entry = BY_DIAL_LENGTH.find((c) => digits.startsWith(c.dial));
    if (!entry) return { country: DEFAULT_COUNTRY, national: sanitised };
    const nsn = digits.slice(entry.dial.length);
    return { country: entry.code, national: entry.trunk ? `0${nsn}` : nsn };
  }

  // Values stored before canonicalisation are UK national numbers.
  return { country: DEFAULT_COUNTRY, national: sanitised };
}

/**
 * Format a canonical value for reading. UK mobiles and the fixed length
 * non-geographic ranges get their usual grouping; other numbers are returned
 * as stored rather than grouped incorrectly.
 */
function formatPhoneDisplay(value) {
  const sanitised = sanitisePhone(value);
  if (!sanitised) return '';
  const parts = splitE164(sanitised);
  if (parts.country !== 'GB') return sanitised;
  const national = parts.national;
  if (UK_MOBILE.test(national)) {
    return `${national.slice(0, 5)} ${national.slice(5)}`;
  }
  if (UK_NON_GEOGRAPHIC.test(national)) {
    return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
  }
  if (UK_LANDLINE_02.test(national)) {
    return `${national.slice(0, 3)} ${national.slice(3, 7)} ${national.slice(7)}`;
  }
  return national;
}


export {
  PHONE_COUNTRIES,
  DEFAULT_COUNTRY,
  toE164,
  isValidPhone,
  phoneErrorMessage,
  splitE164,
  formatPhoneDisplay,
};
